import { bsMonthName, formatBs, fromAd, isProvisional, type BsDate } from "./bs";
import { serverNow } from "./clock";
import { toNepaliDigits, type Lang } from "./money";

// Nepal is UTC+5:45 all year.
const nepalOffsetMs = (5 * 60 + 45) * 60 * 1000;

const months: Record<Lang, string[]> = {
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  ne: ["जनवरी", "फेब्रुअरी", "मार्च", "अप्रिल", "मे", "जुन", "जुलाई", "अगस्ट", "सेप्टेम्बर", "अक्टोबर", "नोभेम्बर", "डिसेम्बर"],
};

const shortMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ms = (instant: Date | number) => (typeof instant === "number" ? instant : instant.getTime());

const local = (s: string, lang: Lang) => (lang === "ne" ? toNepaliDigits(s) : s);

/** The Nepal calendar date of an instant in AD, month 1 to 12. */
function adDate(instant: Date | number): { year: number; month: number; day: number } {
  const d = new Date(ms(instant) + nepalOffsetMs);

  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/** The BS date of an instant in Nepal, or null outside the calendar table. */
function bsDate(instant: Date | number): BsDate | null {
  try {
    return fromAd(instant);
  } catch {
    return null;
  }
}

/**
 * Formats the Nepal calendar date of an instant in AD: "2 July 2026", or
 * "२ जुलाई २०२६" in Nepali. Show people formatDate; this is for an AD date printed
 * beside a BS one, and for periods the server counts in AD months.
 */
export function formatAdDate(instant: Date | number, lang: Lang): string {
  const d = adDate(instant);

  return local(`${d.day} ${months[lang][d.month - 1]} ${d.year}`, lang);
}

/**
 * Formats the Nepal calendar date of an instant for the reader: Bikram Sambat in
 * Nepali, "३ कात्तिक २०८३", and AD in English, "20 October 2026" (ADR-0022). A BS date
 * in a provisional year is marked "(अनुमानित)"; outside the BS table Nepali falls back
 * to AD, marked "(ई.सं.)". Use this for every date shown to people; bs.FormatDate in
 * the backend writes the same, so a text message and the screen agree.
 */
export function formatDate(instant: Date | number, lang: Lang): string {
  const bs = lang === "ne" ? bsDate(instant) : null;
  if (bs) return isProvisional(bs.year) ? `${formatBs(bs, lang)} (अनुमानित)` : formatBs(bs, lang);

  return lang === "ne" ? `${formatAdDate(instant, lang)} (ई.सं.)` : formatAdDate(instant, lang);
}

/**
 * The Nepal calendar date of an instant in the reader's calendar, month 1 to 12: BS in
 * Nepali, AD in English and outside the BS table. For placing month ticks on a chart.
 */
export function readerDate(instant: Date | number, lang: Lang): { year: number; month: number; day: number } {
  return (lang === "ne" && bsDate(instant)) || adDate(instant);
}

/** A short day and month for chart axes and ranges: "13 Oct", or "२७ असोज" in Nepali. */
export function formatDayMonth(instant: Date | number, lang: Lang): string {
  const bs = lang === "ne" ? bsDate(instant) : null;
  if (bs) return local(`${bs.day} ${bsMonthName(bs.month, lang)}`, lang);

  const d = adDate(instant);
  return local(`${d.day} ${lang === "ne" ? months.ne[d.month - 1] : shortMonths[d.month - 1]}`, lang);
}

/** A month for chart axes: "Oct" or "Oct 2026", or "असोज" or "असोज २०८३" in Nepali. */
export function formatMonth(instant: Date | number, lang: Lang, withYear: boolean): string {
  const bs = lang === "ne" ? bsDate(instant) : null;
  if (bs) return local(bsMonthName(bs.month, lang) + (withYear ? ` ${bs.year}` : ""), lang);

  const d = adDate(instant);
  return local((lang === "ne" ? months.ne[d.month - 1] : shortMonths[d.month - 1]) + (withYear ? ` ${d.year}` : ""), lang);
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Formats the Nepal clock time of an instant, 24-hour: "06:30", or "०६:३०" in Nepali. */
export function formatNepalTime(instant: Date | number, lang: Lang): string {
  const d = new Date(ms(instant) + nepalOffsetMs);

  return local(`${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`, lang);
}

/** Returns the Nepal calendar date of an instant as "YYYY-MM-DD", in AD. */
export function nepalIsoDate(instant: Date | number): string {
  const d = adDate(instant);

  return `${d.year}-${pad2(d.month)}-${pad2(d.day)}`;
}

/** Returns today's date in Nepal as "YYYY-MM-DD" by the api's clock, whatever the device's time zone. */
export function nepalToday(now: Date | number = serverNow()): string {
  return nepalIsoDate(now);
}
